import { useEffect, useMemo, useState } from "react";
import { useAtom } from "jotai/react";
import { useForm, UseFormReturn } from "react-hook-form";
import { toast } from "sonner-native";

import { ApplicationTypeEnumType } from "@libs/api/patients";
import { useCreateApplication } from "@/data/mutations/applicationsMutations";
import { useSyncEligibilityResultsMutation } from "@/data/mutations/eligibilityResultsMutations";
import { useUpdateCurrentPatientMutation } from "@/data/mutations/patientsMutations";
import { useListEligibilityCategoriesQuery } from "@/data/queries/eligibilityCategoriesQueries";
import {
  useCurrentHouseholdMemberQuery,
  useListHouseholdMembersQuery,
} from "@/data/queries/householdMembersQueries";
import { useGetHouseholdQuery } from "@/data/queries/householdsQueries";
import {
  CreateApplicationFormType,
  CreateApplicationResponseType,
  CreateApplicationType,
  excludeConflictingSelections,
  findProgramConflict,
  ProgramConflictType,
} from "@/domain/applications";
import { lastSyncedEligibilityDataAtom } from "@/stores/atoms";
import { determineEligibleFederalGroups } from "@/utils/eligibilityValidation";

import { useHouseholdFpl } from "./useHouseholdFpl";
import { usePatientUSState } from "./usePatientUSState";
import { useTranslator } from "./useTranslate";

type UseApplicationFormPropsType = {
  onSubmit: (
    applicationIds: string[],
    applicationTypes: ApplicationTypeEnumType[],
  ) => void | Promise<void>;
};

export type MemberSelectionRequestType = {
  applicationType: ApplicationTypeEnumType;
  memberIds: string[];
  // Section-level selections also drive the accordion; member-level ones don't
  isSectionToggle?: boolean;
};

type PendingProgramConflictType = ProgramConflictType & {
  isSectionToggle: boolean;
};

export const useApplicationForm = ({
  onSubmit,
}: UseApplicationFormPropsType) => {
  const { t } = useTranslator();
  const { mutateAsync: createApplication } = useCreateApplication();
  const { data: householdMembers } = useListHouseholdMembersQuery();
  const [isLoading, setIsLoading] = useState(false);
  const [openAccordions, setOpenAccordions] = useState<string[]>([]);
  const [pendingProgramConflict, setPendingProgramConflict] =
    useState<PendingProgramConflictType | null>(null);
  const { mutateAsync: syncEligibilityResults } =
    useSyncEligibilityResultsMutation();
  const { mutateAsync: updateCurrentPatient } =
    useUpdateCurrentPatientMutation();

  // Persisted across app refreshes to prevent redundant eligibility result syncs
  const [lastSyncedEligibilityData, setLastSyncedEligibilityData] = useAtom(
    lastSyncedEligibilityDataAtom,
  );

  const { data: currentUserHouseholdMember } = useCurrentHouseholdMemberQuery();

  const primaryMemberUSState = usePatientUSState();

  const { data: household } = useGetHouseholdQuery();

  const householdIncome = household?.householdIncome;
  const householdFpl = useHouseholdFpl();

  // Calulate how much % the household income is of the FPL, if both values are available
  const incomeAsPercentageOfFpl =
    householdIncome && householdFpl
      ? Math.round((householdIncome / householdFpl) * 100)
      : null;

  const { data: availableEligibilityCategories } =
    useListEligibilityCategoriesQuery({
      filters: {
        usState: primaryMemberUSState ?? undefined,
        fplPercentage: incomeAsPercentageOfFpl ?? undefined,
      },
      enabled:
        Boolean(primaryMemberUSState) && incomeAsPercentageOfFpl !== null,
    });

  // Calculate eligible household members for each application type
  const eligibleHouseholdMembersByType = useMemo(() => {
    if (!householdMembers || !availableEligibilityCategories) {
      return {
        MEDICAID: [],
        MEDICARE: [],
        MARKETPLACE: [],
      };
    }

    const result: Record<
      ApplicationTypeEnumType,
      { memberId: string; eligibilityCategoryId: string }[]
    > = {
      MEDICAID: [],
      MEDICARE: [],
      MARKETPLACE: [],
    };

    for (const member of householdMembers) {
      const eligibleFederalGroups = determineEligibleFederalGroups(member);

      // Find matching eligibility categories for this member
      const matchingCategories = availableEligibilityCategories.filter(
        category => eligibleFederalGroups.includes(category.federalGroup),
      );

      // Group by application type
      for (const category of matchingCategories) {
        const existingEntry = result[category.applicationType].find(
          entry => entry.memberId === member.id,
        );

        if (!existingEntry) {
          result[category.applicationType].push({
            memberId: member.id,
            eligibilityCategoryId: category.id,
          });
        }
      }
    }

    // Members eligible for Medicaid should not be marked as Marketplace-eligible,
    // even if the data qualifies them for both programs.
    const medicaidMemberIds = new Set(
      result.MEDICAID.map(entry => entry.memberId),
    );
    result.MARKETPLACE = result.MARKETPLACE.filter(
      entry => !medicaidMemberIds.has(entry.memberId),
    );

    return result;
  }, [householdMembers, availableEligibilityCategories]);

  const createMedicaidApplicationForm = useForm<CreateApplicationFormType>({
    defaultValues: {
      applicationType: "MEDICAID",
      householdMembers: [],
      applicationStatus: "DRAFT",
    },
  });

  const createMedicareApplicationForm = useForm<CreateApplicationFormType>({
    defaultValues: {
      applicationType: "MEDICARE",
      householdMembers: [],
      applicationStatus: "DRAFT",
    },
  });

  const createMarketplaceApplicationForm = useForm<CreateApplicationFormType>({
    defaultValues: {
      applicationType: "MARKETPLACE",
      householdMembers: [],
      applicationStatus: "DRAFT",
    },
  });

  const applicationFormsByType: Record<
    ApplicationTypeEnumType,
    UseFormReturn<CreateApplicationFormType>
  > = {
    MEDICAID: createMedicaidApplicationForm,
    MEDICARE: createMedicareApplicationForm,
    MARKETPLACE: createMarketplaceApplicationForm,
  };

  // Set form values whenever eligible members are computed (pure client-side, no API call)
  useEffect(() => {
    const defaultSelections = excludeConflictingSelections({
      MEDICAID: eligibleHouseholdMembersByType.MEDICAID.map(
        entry => entry.memberId,
      ),
      MEDICARE: eligibleHouseholdMembersByType.MEDICARE.map(
        entry => entry.memberId,
      ),
      MARKETPLACE: eligibleHouseholdMembersByType.MARKETPLACE.map(
        entry => entry.memberId,
      ),
    });

    createMedicaidApplicationForm.setValue(
      "householdMembers",
      defaultSelections.MEDICAID,
    );
    createMedicareApplicationForm.setValue(
      "householdMembers",
      defaultSelections.MEDICARE,
    );
    createMarketplaceApplicationForm.setValue(
      "householdMembers",
      defaultSelections.MARKETPLACE,
    );
  }, [
    eligibleHouseholdMembersByType,
    createMedicaidApplicationForm,
    createMedicareApplicationForm,
    createMarketplaceApplicationForm,
  ]);

  // Sync eligibility results to the server when eligible members are fully computed
  useEffect(() => {
    // Empty member/category lists are treated as "not ready" rather than "no
    // matches": both queries can transiently resolve empty mid-refresh, and a
    // sync fired from such a render wipes previously stored results (#504)
    const isReadyToSync =
      Boolean(currentUserHouseholdMember?.householdId) &&
      Boolean(householdMembers?.length) &&
      Boolean(availableEligibilityCategories?.length) &&
      incomeAsPercentageOfFpl !== null;

    if (!isReadyToSync) return;

    // Guaranteed defined by isReadyToSync
    const currentUserHouseholdId = currentUserHouseholdMember!.householdId;

    const allResults = Object.entries(eligibleHouseholdMembersByType).flatMap(
      ([applicationType, members]) =>
        members.map(entry => ({
          householdMemberId: entry.memberId,
          eligibilityCategoryId: entry.eligibilityCategoryId,
          applicationType: applicationType as ApplicationTypeEnumType,
        })),
    );

    // Never send an empty result set — it would delete every stored result
    // for the household, and a later render with real matches may not occur
    if (allResults.length === 0) return;

    // Serialize to detect actual data changes and skip redundant syncs
    const serialized = JSON.stringify(eligibleHouseholdMembersByType);
    if (serialized === lastSyncedEligibilityData) return;

    // Persisted via MMKV to survive app refreshes
    setLastSyncedEligibilityData(serialized);

    void syncEligibilityResults({
      householdId: currentUserHouseholdId,
      body: { eligibilityResults: allResults },
    }).catch(() => {
      // Reset so a retry can happen on next render
      setLastSyncedEligibilityData("");
    });
  }, [
    eligibleHouseholdMembersByType,
    householdMembers,
    availableEligibilityCategories,
    incomeAsPercentageOfFpl,
    currentUserHouseholdMember,
    syncEligibilityResults,
    lastSyncedEligibilityData,
    setLastSyncedEligibilityData,
  ]);

  const prepareApplicationPayloads = (): CreateApplicationType[] => {
    const medicaidData = createMedicaidApplicationForm.getValues();
    const medicareData = createMedicareApplicationForm.getValues();
    const marketplaceData = createMarketplaceApplicationForm.getValues();

    const applicationsToCreate = [
      medicaidData,
      medicareData,
      marketplaceData,
    ].filter(data => data.householdMembers && data.householdMembers.length > 0);

    return applicationsToCreate.map(data => {
      const allEligible =
        eligibleHouseholdMembersByType[data.applicationType] ?? [];

      // For Marketplace, there's only one category per state — use it directly.
      const marketplaceCategoryId = availableEligibilityCategories?.find(
        c => c.applicationType === "MARKETPLACE",
      )?.id;

      const applyingMembers = data.householdMembers.map(memberId => {
        const match = allEligible.find(e => e.memberId === memberId);
        return {
          memberId,
          eligibilityCategoryId:
            data.applicationType === "MARKETPLACE"
              ? marketplaceCategoryId
              : match?.eligibilityCategoryId,
        };
      });

      return { ...data, householdMembers: applyingMembers };
    });
  };

  const handleSubmit = async (
    applicationsToCreate: readonly CreateApplicationType[],
  ) => {
    const applicationPromises = applicationsToCreate.map(application =>
      createApplication(application),
    );

    setIsLoading(true);

    try {
      const results = await Promise.allSettled(applicationPromises);

      const failures = results.filter(result => result.status === "rejected");
      if (failures.length > 0) {
        toast.error(t("common:errors.unableToCreateApplications"));
        return;
      }

      const applicationIds = results
        .filter(
          (a): a is PromiseFulfilledResult<CreateApplicationResponseType> =>
            a.status === "fulfilled",
        )
        .map(a => a.value.application.id);

      if (applicationIds.length > 0) {
        await updateCurrentPatient({
          patientData: {
            applicationLastStep: null,
          },
        });

        const applicationTypes = applicationsToCreate.map(
          d => d.applicationType,
        );
        await onSubmit(applicationIds, applicationTypes);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const setSectionOpen = (
    applicationType: ApplicationTypeEnumType,
    isOpen: boolean,
  ) => {
    setOpenAccordions(prev => {
      const withoutSection = prev.filter(item => item !== applicationType);
      return isOpen ? [...withoutSection, applicationType] : withoutSection;
    });
  };

  const applyMemberSelection = ({
    applicationType,
    memberIds,
    isSectionToggle,
  }: MemberSelectionRequestType) => {
    applicationFormsByType[applicationType].setValue(
      "householdMembers",
      memberIds,
      { shouldDirty: true },
    );

    if (isSectionToggle) {
      setSectionOpen(applicationType, memberIds.length > 0);
    }
  };

  /**
   * Single entry point for every selection the patient makes, so a conflict
   * cannot slip in through a path that skips the check.
   */
  const handleMemberSelectionRequest = (
    request: MemberSelectionRequestType,
  ) => {
    const conflict = findProgramConflict({
      newProgram: request.applicationType,
      memberIdsToSelect: request.memberIds,
      selectedMemberIdsByProgram: {
        MEDICAID: createMedicaidApplicationForm.getValues("householdMembers"),
        MEDICARE: createMedicareApplicationForm.getValues("householdMembers"),
        MARKETPLACE:
          createMarketplaceApplicationForm.getValues("householdMembers"),
      },
      medicaidEligibleMemberIds: eligibleHouseholdMembersByType.MEDICAID.map(
        entry => entry.memberId,
      ),
    });

    if (!conflict) {
      applyMemberSelection(request);
      return;
    }

    // Members that don't conflict are applied straight away so a select-all
    // isn't lost to the one member the warning is actually about
    applyMemberSelection({
      ...request,
      memberIds: request.memberIds.filter(
        memberId => !conflict.conflictingMemberIds.includes(memberId),
      ),
    });

    setPendingProgramConflict({
      ...conflict,
      isSectionToggle: Boolean(request.isSectionToggle),
    });
  };

  const handleSwitchProgram = () => {
    if (!pendingProgramConflict) return;

    const {
      newProgram,
      conflictingPrograms,
      memberIdsToSelect,
      conflictingMemberIds,
      isSectionToggle,
    } = pendingProgramConflict;

    for (const program of conflictingPrograms) {
      const conflictingForm = applicationFormsByType[program];
      conflictingForm.setValue(
        "householdMembers",
        conflictingForm
          .getValues("householdMembers")
          .filter(memberId => !conflictingMemberIds.includes(memberId)),
        { shouldDirty: true },
      );
    }

    applyMemberSelection({
      applicationType: newProgram,
      memberIds: memberIdsToSelect,
      isSectionToggle,
    });

    setPendingProgramConflict(null);
  };

  const handleKeepProgram = () => {
    setPendingProgramConflict(null);
  };

  // Calculate selected programs and members for counter
  const medicaidMembers =
    createMedicaidApplicationForm.watch("householdMembers") || [];
  const medicareMembers =
    createMedicareApplicationForm.watch("householdMembers") || [];
  const marketplaceMembers =
    createMarketplaceApplicationForm.watch("householdMembers") || [];

  const selectedProgramsCount = [
    medicaidMembers.length > 0,
    medicareMembers.length > 0,
    marketplaceMembers.length > 0,
  ].filter(Boolean).length;

  // The header reports eligibility, not the selection: conflicting defaults are
  // pruned from the selection but the member stays eligible for both programs
  const eligibleProgramsCount = Object.values(
    eligibleHouseholdMembersByType,
  ).filter(members => members.length > 0).length;

  const uniqueHouseholdMembers = new Set([
    ...medicaidMembers,
    ...medicareMembers,
    ...marketplaceMembers,
  ]);

  const isContinueDisabled =
    medicaidMembers.length === 0 &&
    medicareMembers.length === 0 &&
    marketplaceMembers.length === 0;

  return {
    forms: {
      medicaid: createMedicaidApplicationForm,
      medicare: createMedicareApplicationForm,
      marketplace: createMarketplaceApplicationForm,
    },
    isLoading,
    isContinueDisabled,
    openAccordions,
    setOpenAccordions,
    handleSubmit,
    handleMemberSelectionRequest,
    pendingProgramConflict,
    handleSwitchProgram,
    handleKeepProgram,
    prepareApplicationPayloads,
    eligibleProgramsCount,
    selectedProgramsCount,
    uniqueHouseholdMembersCount: uniqueHouseholdMembers.size,
  };
};
